import {put, call, all} from 'redux-saga/effects';
import Discount from "../../entities/discount";
import * as Actions from "../actions/discountActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DiscountAction = {
    discountId: string;
    discount: Discount;
    discounts: Discount[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDiscount(action: DiscountAction) {
	const session = tabSession.captureGeneration();
	try {
		const discount: Discount = yield call(api.findOne, 'discount', action.discountId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_FETCH_ONE_SUCCESS, discountId: action.discountId, discount, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DISCOUNT_FETCH_ONE_FAILURE, discountId: action.discountId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DiscountAction} action
 */
export function* saveDiscount(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DISCOUNT_SAVE saga', action.discount?.id);

		if (action.discount === undefined || action.discount === null) {
			console.error('saveDiscount received a null');
			throw new Error('saveDiscount received a null');
		}

		if (!(action.discount instanceof Discount)) {
			action.discount = new Discount(action.discount);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.discount.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.discount);
			const addResult = yield call(idb.add, 'discount', action.discount);
			if (addResult.isErr()) {
				console.error(`⮂ saveDiscount: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertDiscountSave(action.discount, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.DISCOUNT_SAVE_FAILURE, discount: action.discount, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.DISCOUNT_SAVE, discount: action.discount, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertDiscountSave(action.discount, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.DISCOUNT_SAVE_FAILURE, discount: action.discount, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {DiscountAction} action
 * @param {Discount[]} action.discounts
 */
export function* saveManyDiscounts(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DISCOUNT_SAVE_MANY saga', action.discounts.map(discount => discount.id).join(','));

        if (!Array.isArray(action.discounts)) {
            console.error('saveManyDiscounts expected an array, received', action.discounts);
            throw new Error('saveManyDiscounts expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveDiscount), under these queue keys
		const dispatchedAddedOns: number[] = action.discounts.map(discount => discount?.addedOn);
		const revertAll = () => action.discounts.map((discount, i) =>
			put(Actions.revertDiscountSave(discount, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Discount[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.discounts.length; i++) {
				const discount = action.discounts[i];
				discount.persisted = false;
				ensureOperationId(discount);
				toSave.push(discount);
			}

			const addResult = yield call(idb.add, 'discount', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyDiscounts: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.DISCOUNT_SAVE_MANY_FAILURE, discounts: action.discounts, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.DISCOUNT_SAVE_MANY, discounts: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.DISCOUNT_SAVE_MANY_FAILURE, discounts: action.discounts, status: 500, err});

			console.error(`Error saving ${action.discounts.length} discounts to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {DiscountAction} action
 */
export function* removeDiscount(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.discount === undefined || action.discount === null || !(action.discount instanceof Discount)) {
			console.error('removeDiscount expected type Discount, received', action.discount);
			reportQueued(false);
			throw new Error('removeDiscount expected type Discount');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.discount.status = 'D';
		action.discount.addedOn = newHRTimestamp();
		action.discount.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.discount);
		const addResult = yield call(idb.add, 'discount', action.discount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDiscount: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.DISCOUNT_REMOVE_FAILURE, discount: action.discount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_REMOVE, discount: action.discount});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DISCOUNT_REMOVE_FAILURE, discount: action.discount, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Discount.
	*
	* @param {DiscountAction} action
	*/
export function* restoreDiscount(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDiscount expected getCurrentUserId() to return a value');
        return;
    }

	if (action.discount === undefined || action.discount === null) {
        console.error('restoreDiscount expected a Discount, received null');
        throw new Error('restoreDiscount expected a Discount, received null');
    }
    else if (!(action.discount instanceof Discount)) action.discount = new Discount(action.discount); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.discount.status = 'U';
		action.discount.addedOn = newHRTimestamp();
		action.discount.addedBy = userId;
		action.discount.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.discount);
		const addResult = yield call(idb.add, 'discount', action.discount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDiscount: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DISCOUNT_RESTORE_FAILURE, discount: action.discount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_RESTORE, discount: action.discount});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DISCOUNT_RESTORE_FAILURE, discount: action.discount, status: 500, err});

		console.error('Error restoring from database', err);
	}
}